import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  FormControl,
  FormControlLabel,
  FormLabel,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import utils from '../../universal/utils';
import adminFetch from '../../universal/adminFetch';
import useIsMountedRef from '../../hooks/useIsMountedRef';

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(3),
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },
  section: {
    marginTop: theme.spacing(3),
  },
  chipRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  weightRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginTop: theme.spacing(1),
    flexWrap: 'wrap',
  },
  weightInput: {
    width: 100,
  },
  status: {
    marginTop: theme.spacing(1),
  },
  actionRow: {
    display: 'flex',
    gap: theme.spacing(2),
    marginTop: theme.spacing(3),
    flexWrap: 'wrap',
  },
}));

export default function TemplateSettings({ userId }) {
  const classes = useStyles();
  const isMountedRef = useIsMountedRef();
  const basePath = useMemo(() => utils.getEdgeBasePath(), []);

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [mode, setMode] = useState('fixed');
  const [activeTemplates, setActiveTemplates] = useState(['original']);
  const [weights, setWeights] = useState({ original: 100 });
  const [knownTemplates, setKnownTemplates] = useState([
    { codename: 'original', displayName: 'Original' },
    { codename: 'minimal', displayName: 'Minimal' },
  ]);
  const [updatedAtMs, setUpdatedAtMs] = useState(null);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const [configRes, registryRes] = await Promise.all([
        adminFetch(`${basePath}/api/v7/admin/settings/template-config/adpass/${userId}`),
        adminFetch(`${basePath}/api/v7/admin/templates/registry/adpass/${userId}`),
      ]);
      const data = await configRes.json();
      const registry = await registryRes.json();
      if (!data?.ok) throw new Error(data?.err || 'Failed to load template config');
      if (!registry?.ok) throw new Error(registry?.err || 'Failed to load template registry');
      if (!isMountedRef.current) return;
      const cfg = data.config || {};
      setMode(cfg.mode || 'fixed');
      setActiveTemplates(Array.isArray(cfg.activeTemplates) ? cfg.activeTemplates : ['original']);
      setWeights(typeof cfg.weights === 'object' && cfg.weights ? cfg.weights : { original: 100 });
      setKnownTemplates(Array.isArray(registry.templates) ? registry.templates : []);
      setUpdatedAtMs(typeof data.updatedAtMs === 'number' ? data.updatedAtMs : null);
    } catch (e) {
      if (isMountedRef.current) setError(e?.message || String(e));
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, [basePath, isMountedRef, userId]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleTemplate = (codename) => {
    setActiveTemplates((prev) => {
      if (prev.includes(codename)) {
        if (prev.length === 1) return prev; // must keep at least one
        return prev.filter((c) => c !== codename);
      }
      return [...prev, codename];
    });
  };

  const handleWeightChange = (codename, value) => {
    const num = parseInt(value, 10);
    setWeights((prev) => ({ ...prev, [codename]: isNaN(num) || num < 1 ? 1 : num }));
  };

  const save = useCallback(async () => {
    if (!userId) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const config = { mode, activeTemplates, weights };
      const res = await adminFetch(
        `${basePath}/api/v7/admin/settings/template-config/adpass/${userId}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ config }),
        }
      );
      const data = await res.json();
      if (!data?.ok) throw new Error(data?.err || 'Failed to save template config');
      if (!isMountedRef.current) return;
      setUpdatedAtMs(Date.now());
      setSuccess('Saved. Changes take effect for new visitors within seconds (5s cache).');
    } catch (e) {
      if (isMountedRef.current) setError(e?.message || String(e));
    } finally {
      if (isMountedRef.current) setSaving(false);
    }
  }, [basePath, isMountedRef, userId, mode, activeTemplates, weights]);

  return (
    <Paper className={classes.paper}>
      <Box className={classes.headerRow}>
        <Box>
          <Typography variant="h6">Public Page Templates</Typography>
          <Typography variant="body2" color="textSecondary">
            Control which homepage design(s) visitors see. Use "fixed" to show one template to
            everyone, or "rotate" for weighted A/B traffic split.
          </Typography>
          {updatedAtMs ? (
            <Typography variant="caption" color="textSecondary">
              Last saved: {new Date(updatedAtMs).toLocaleString()}
            </Typography>
          ) : null}
        </Box>
        <Button
          variant="outlined"
          size="small"
          onClick={() => setOpen((v) => !v)}
          startIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        >
          {open ? 'Hide' : 'Show'}
        </Button>
      </Box>

      <Collapse in={open} timeout="auto" unmountOnExit>
        {loading ? (
          <Box mt={2}>
            <CircularProgress size={20} />
          </Box>
        ) : (
          <>
            {/* Mode */}
            <Box className={classes.section}>
              <FormControl component="fieldset">
                <FormLabel component="legend">
                  <Typography variant="subtitle2">Serving mode</Typography>
                </FormLabel>
                <RadioGroup
                  row
                  value={mode}
                  onChange={(e) => setMode(e.target.value)}
                >
                  <FormControlLabel
                    value="fixed"
                    control={<Radio size="small" />}
                    label={
                      <Typography variant="body2">
                        Fixed — all visitors see the same template
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    value="rotate"
                    control={<Radio size="small" />}
                    label={
                      <Typography variant="body2">
                        Rotate — weighted random per visitor (sticky cookie)
                      </Typography>
                    }
                  />
                </RadioGroup>
              </FormControl>
            </Box>

            {/* Active templates */}
            <Box className={classes.section}>
              <Typography variant="subtitle2" gutterBottom>
                {mode === 'fixed'
                  ? 'Active template (first enabled will be served)'
                  : 'Templates in rotation'}
              </Typography>
              <Box className={classes.chipRow}>
                {knownTemplates.map((template) => {
                  const codename = template.codename;
                  return (
                  <Chip
                    key={codename}
                    label={template.displayName ? `${template.displayName} (${codename})` : codename}
                    clickable
                    color={activeTemplates.includes(codename) ? 'primary' : 'default'}
                    onClick={() => toggleTemplate(codename)}
                    variant={activeTemplates.includes(codename) ? 'default' : 'outlined'}
                  />
                  );
                })}
              </Box>
              <Typography variant="caption" color="textSecondary">
                Click a chip to enable/disable. At least one must be active.
              </Typography>
            </Box>

            {/* Weights (rotate mode only) */}
            {mode === 'rotate' && (
              <Box className={classes.section}>
                <Typography variant="subtitle2" gutterBottom>
                  Traffic weights
                </Typography>
                {activeTemplates.map((codename) => (
                  <Box key={codename} className={classes.weightRow}>
                    <Typography variant="body2" style={{ minWidth: 80 }}>
                      {codename}
                    </Typography>
                    <TextField
                      className={classes.weightInput}
                      type="number"
                      size="small"
                      variant="outlined"
                      label="Weight"
                      value={weights[codename] ?? 1}
                      onChange={(e) => handleWeightChange(codename, e.target.value)}
                      inputProps={{ min: 1, step: 1 }}
                    />
                    {(() => {
                      const total = activeTemplates.reduce(
                        (s, c) => s + (weights[c] ?? 1),
                        0
                      );
                      const pct =
                        total > 0
                          ? Math.round(((weights[codename] ?? 1) / total) * 100)
                          : 0;
                      return (
                        <Typography variant="body2" color="textSecondary">
                          ≈ {pct}% of traffic
                        </Typography>
                      );
                    })()}
                  </Box>
                ))}
              </Box>
            )}

            {/* Actions */}
            <Box className={classes.actionRow}>
              <Button
                variant="contained"
                color="primary"
                onClick={save}
                disabled={saving || loading || !userId}
              >
                {saving ? <CircularProgress size={18} /> : 'Save'}
              </Button>
              <Button variant="outlined" onClick={load} disabled={saving || loading || !userId}>
                Refresh
              </Button>
            </Box>

            {error ? (
              <Typography className={classes.status} variant="body2" color="error">
                {error}
              </Typography>
            ) : null}
            {success ? (
              <Typography
                className={classes.status}
                variant="body2"
                style={{ color: '#1b5e20' }}
              >
                {success}
              </Typography>
            ) : null}
          </>
        )}
      </Collapse>
    </Paper>
  );
}
